import { useEffect } from 'react';

import { useQuery } from '@apollo/client';
import { BehaviorSubject, from } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  switchMap,
} from 'rxjs/operators';

import ADMINDASHBOARD_SEARCH_ORGANIZATION_USERS_QUERY from './graphql/AdminDashboardSearchUsersQuery.b2b.graphql';
import type {
  AdminDashboardSearchUsersQuery,
  AdminDashboardSearchUsersQueryVariables,
} from './graphql/types/AdminDashboardSearchUsersQuery';

const _searchResultBehavior = new BehaviorSubject<string>('');
const searchResultObservable$ = _searchResultBehavior.asObservable();

const formatSearchOrgUsersData = ({
  data,
  slug,
}: {
  data?: AdminDashboardSearchUsersQuery;
  slug: string;
}) =>
  data?.userOrganizationSearch?.edges.map(user => {
    const userNode = user?.node;

    return {
      id: userNode?.user?.originalId || null,
      title: userNode?.user?.fullName || userNode?.user?.username || '',
      subTitle: userNode?.team ? `Ruta ${userNode.team.name}` : '',
      imageUrl: userNode?.user?.profilePictureUrl || '',
      url: `/org/${slug}/panel/estadisticas/especificas/${userNode?.user?.username}/`,
      type: 'user',
    };
  }) || [];

const useSearchOrganizationUsers = ({
  searchKey,
  slug,
  itemsPerFetch,
}: {
  searchKey: string;
  slug: string;
  itemsPerFetch: number;
}) => {
  // Front end actual search query
  const {
    data: searchOrgUsers,
    loading: loadingSearchOrgUsers,
    refetch,
  } = useQuery<
    AdminDashboardSearchUsersQuery,
    AdminDashboardSearchUsersQueryVariables
  >(ADMINDASHBOARD_SEARCH_ORGANIZATION_USERS_QUERY, {
    context: { clientName: 'b2b' },
    notifyOnNetworkStatusChange: true,
    variables: {
      slug,
      searchKey: '',
      first: itemsPerFetch,
    },
    skip: !searchKey || !slug,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentional
  useEffect(() => {
    _searchResultBehavior.next(searchKey);
    const subject = searchResultObservable$
      .pipe(
        filter(value => value.length > 1),
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(() =>
          from(
            refetch({
              searchKey,
              first: itemsPerFetch,
              slug,
            }),
          ),
        ),
      )
      .subscribe();

    return () => {
      subject.unsubscribe();
    };
  }, [searchKey]);

  return {
    userResults: formatSearchOrgUsersData({
      data: searchOrgUsers,
      slug,
    }),
    loadingUsers: loadingSearchOrgUsers,
    totalUsersCount: searchOrgUsers?.userOrganizationSearch?.totalCount || 0,
  };
};

export default useSearchOrganizationUsers;
